import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Building2, User, Zap, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "../App";
import {
  PRICING_PLANS,
  getAssignedPlan,
  ROLE_TO_PLAN,
  getTrialDaysCopy,
} from "../constants/plans";
import { getActivationJourneySteps } from "../constants/activationJourney";
import { getResumeActivationCta } from "../constants/activationResume";
import { api } from "../services/apiClient";
import { useSubscriptionActivation } from "../hooks/useSubscriptionActivation";

const PLAN_ICON_BY_ID = {
  free: User,
  pro: Zap,
  broker: Building2,
  developer: Building2,
};

const Pricing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [accountUser, setAccountUser] = useState(user || null);
  const defaultTrialDays =
    PRICING_PLANS.find((plan) => Boolean(plan.trialDays))?.trialDays || 14;

  useEffect(() => {
    let mounted = true;

    const syncAccountUser = async () => {
      if (!user) {
        if (mounted) setAccountUser(null);
        return;
      }

      if (mounted) setAccountUser(user);

      try {
        const response = await api.get("/auth/me");
        if (!response.ok) return;
        const data = await response.json();
        if (mounted) setAccountUser(data);
      } catch {
        // Keep auth context as fallback.
      }
    };

    syncAccountUser();
    return () => {
      mounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (!location.hash) return;

    const targetId = location.hash.replace("#", "");
    const scrollToTarget = () => {
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    // Wait for pricing sections to render before attempting anchor scroll.
    const timeoutId = window.setTimeout(scrollToTarget, 40);
    return () => window.clearTimeout(timeoutId);
  }, [location.hash]);

  const activeUser = accountUser || user;
  const assignedPlan = activeUser ? getAssignedPlan(activeUser) : null;
  const hasUsedTrial = !!activeUser?.trial_used;
  const activationJourneySteps = getActivationJourneySteps({
    planLabel: assignedPlan ? PRICING_PLANS.find((plan) => plan.planId === assignedPlan)?.name : null,
    trialDaysCopy: getTrialDaysCopy(defaultTrialDays),
    hasUsedTrial,
  });
  const resumeActivationCta = getResumeActivationCta({
    isAuthenticated: Boolean(activeUser),
  });
  const { activateSubscription, activatingPlanId } = useSubscriptionActivation({
    user: activeUser,
    navigate,
  });

  return (
    <div className="min-h-screen">
      <Header />

      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              Planes y Precios
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Elige la cuenta que mejor refleja cómo operas hoy. El rol define
              el plan, los límites y las herramientas disponibles. Los planes
              de pago incluyen {getTrialDaysCopy()} de prueba. El periodo de
              prueba se habilita una sola vez por cuenta.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-4xl mx-auto text-left">
              {activationJourneySteps.map((step) => (
                <div
                  key={step.key}
                  className="rounded-2xl border border-border bg-background/80 backdrop-blur px-4 py-4"
                >
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                    {step.eyebrow}
                  </p>
                  <p className="font-semibold">{step.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Planes Unificados */}
      <div id="planes" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16 scroll-mt-24">
        <div className="text-center mb-12">
          <h2
            className="text-2xl md:text-3xl font-bold mb-3"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Elige tu cuenta ideal
          </h2>
          <p className="text-muted-foreground">
            Básico, Pro, Asesor Inmobiliario o Desarrolladora. La activación y
            el alcance operativo dependen del rol y del estado de suscripción.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {PRICING_PLANS.map((plan, index) => {
            const Icon = PLAN_ICON_BY_ID[plan.planId] || User;
            const isCurrentPlan = !!activeUser && assignedPlan === plan.planId;
            const isAdmin = activeUser?.role === "admin";
            const isPlanMismatch =
              !!activeUser && !isAdmin && plan.planId !== (ROLE_TO_PLAN[activeUser.role] || "free");
            const buttonLabel = isCurrentPlan
              ? "Plan asignado"
              : isAdmin
                ? "Cuenta admin"
                : activeUser
                  ? plan.planId === "free"
                    ? "Plan Básico activo"
                    : isPlanMismatch
                      ? "Disponible con otro rol"
                      : hasUsedTrial
                        ? "Activar suscripción"
                        : `Empezar prueba (${defaultTrialDays} días)`
                  : plan.planId === "free"
                    ? "Crear cuenta gratis"
                    : `Crear cuenta y activar ${defaultTrialDays} días`;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative bg-background rounded-lg p-6 ${
                  isCurrentPlan
                    ? "border-2 border-primary shadow-xl ring-2 ring-primary/20"
                    : plan.highlighted
                      ? "border-2 border-accent shadow-xl"
                      : "border border-border"
                }`}
                data-testid={`plan-card-${plan.planId}`}
              >
                {isCurrentPlan && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span
                      className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-semibold"
                      data-testid="current-plan-badge"
                    >
                      Tu plan actual
                    </span>
                  </div>
                )}
                {!isCurrentPlan && plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-accent text-white px-4 py-1 rounded-full text-xs font-semibold">
                      Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3
                    className="text-2xl font-bold mb-1"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {plan.name}
                  </h3>
                  <p className="text-sm text-muted-foreground font-medium mb-2">
                    {plan.subtitle}
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    {plan.description}
                  </p>
                  {plan.trialDays ? (
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-primary mb-3">
                      Prueba de {plan.trialDays} días
                    </p>
                  ) : null}
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-4xl font-bold">
                      ${plan.price.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground">
                      MXN/{plan.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full"
                  variant={
                    isCurrentPlan
                      ? "secondary"
                      : plan.highlighted
                        ? "default"
                        : "outline"
                  }
                  disabled={isCurrentPlan || isAdmin}
                  onClick={() =>
                    activateSubscription({ planId: plan.planId, role: plan.role })
                  }
                  data-testid={`plan-btn-${plan.planId}`}
                >
                  {isCurrentPlan
                    ? "Plan asignado"
                    : isAdmin
                      ? "Cuenta admin"
                      : activatingPlanId === plan.planId
                        ? "Redirigiendo..."
                      : activeUser
                        ? plan.planId === "free"
                          ? "Plan Básico activo"
                          : hasUsedTrial
                            ? "Activar suscripción"
                            : `Empezar prueba (${defaultTrialDays} días)`
                        : plan.cta}
                </Button>
                <p className="text-xs text-muted-foreground mt-3 text-center min-h-[2.5rem]">
                  {!activeUser
                    ? plan.planId === "free"
                      ? "Registro inmediato y acceso sin tarjeta."
                      : `Registro, activación y ${getTrialDaysCopy(defaultTrialDays)} si tu cuenta es elegible.`
                    : isCurrentPlan
                      ? "Este es el plan operativo asignado a tu cuenta actual."
                      : isPlanMismatch
                        ? "Para usar este plan necesitas registrarte con el rol correspondiente."
                        : hasUsedTrial
                          ? "Tu prueba ya fue usada; puedes reactivar o continuar con la suscripción."
                          : "La activación abre Stripe y habilita tu cuenta al terminar el flujo."}
                </p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 rounded-2xl border border-border bg-background p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
              {resumeActivationCta.eyebrow}
            </p>
            <p className="text-sm font-semibold">{resumeActivationCta.title}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {resumeActivationCta.description}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate(resumeActivationCta.href)}>
            {resumeActivationCta.buttonLabel}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* FAQ Section con Acordeón */}
      <div id="faqs" className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-16 scroll-mt-24">
        <h2
          className="text-2xl md:text-3xl font-bold mb-8 text-center"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          Preguntas Frecuentes
        </h2>

        <Accordion type="single" collapsible className="w-full space-y-2">
          <AccordionItem
            value="item-1"
            className="bg-background border border-border rounded-lg px-6"
          >
            <AccordionTrigger
              className="font-bold hover:no-underline"
              data-testid="faq-planes"
            >
              ¿Qué plan me corresponde según mi tipo de cuenta?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground">
              <p className="mb-2">
                Para mantener claridad operativa, el plan se asigna según tu rol:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Básico:</strong> pensado para descubrir oportunidades,
                  guardar favoritos y publicar una propiedad.
                </li>
                <li>
                  <strong>Pro:</strong> pensado para propietarios que quieren
                  publicar, medir desempeño y operar hasta 10 propiedades.
                </li>
                <li>
                  <strong>Asesor Inmobiliario:</strong> para brokers que
                  convierten inventario en seguimiento comercial, leads y comisiones.
                </li>
                <li>
                  <strong>Desarrolladora:</strong> diseñado para equipos que
                  administran desarrollos, unidades y distribución comercial.
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-2"
            className="bg-background border border-border rounded-lg px-6"
          >
            <AccordionTrigger
              className="font-bold hover:no-underline"
              data-testid="faq-capacidades"
            >
              ¿Qué desbloquea cada plan en la práctica?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground">
              <p className="mb-2">
                Esto es lo que habilita cada cuenta desde el día 1:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Básico:</strong> descubrimiento, favoritos, contacto y <strong>1 propiedad gratuita</strong>.
                </li>
                <li>
                  <strong>Pro:</strong> hasta <strong>10 propiedades publicadas</strong>, hasta <strong>5 propiedades destacadas</strong>, descripción con IA y solicitudes.
                </li>
                <li>
                  <strong>Asesor Inmobiliario:</strong> propiedades y destacadas <strong>ilimitadas</strong>, acceso a
                  Inventario para Colaboración, co-comercialización, pipeline de leads y
                  comisiones.
                </li>
                <li>
                  <strong>Desarrolladora:</strong> desarrollos, unidades y propiedades <strong>ilimitados</strong>, inventario para brokers y métricas clave.
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-3"
            className="bg-background border border-border rounded-lg px-6"
          >
            <AccordionTrigger
              className="font-bold hover:no-underline"
              data-testid="faq-colisting"
            >
              ¿Quién puede entrar a Inventario para Colaboración y usar co-listing?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground">
              El acceso está diseñado para operaciones reales: solo
              <strong> brokers con suscripción activa</strong> y
              <strong> administradores</strong> pueden entrar a
              Inventario para Colaboración. El co-listing funciona cuando una propiedad
              tiene representación habilitada y todavía hay espacios
              disponibles.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-4"
            className="bg-background border border-border rounded-lg px-6"
          >
            <AccordionTrigger
              className="font-bold hover:no-underline"
              data-testid="faq-pagos"
            >
              ¿Cómo funciona la prueba de 14 días y la facturación?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground">
              Los planes de pago (Pro, Asesor Inmobiliario y Desarrolladora)
              incluyen una prueba de {getTrialDaysCopy()} por cuenta elegible. Al iniciar,
              configuras tu suscripción en Stripe y al terminar la prueba
              comienza la facturación mensual del plan correspondiente.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-5"
            className="bg-background border border-border rounded-lg px-6"
          >
            <AccordionTrigger
              className="font-bold hover:no-underline"
              data-testid="faq-cambiar-plan"
            >
              ¿Puedo cambiar o cancelar mi plan?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground">
              <p className="mb-2">
                Puedes activar, renovar o retomar tu suscripción desde Pricing
                o Mi Cuenta en minutos.
              </p>
              <p>
                Si cancelas o tu pago queda inactivo, las funciones premium se
                pausan. Tu información se conserva, puedes seguir viendo y
                gestionando propiedades existentes, pero no crear nuevas fuera
                del límite o usar funciones premium. La prueba gratuita aplica una sola vez por cuenta
                y no se vuelve a habilitar aunque la suscripción se haya
                cancelado o quede inactiva por falta de pago. Si necesitas otro
                tipo de plan, debes usar el rol correspondiente.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-6"
            className="bg-background border border-border rounded-lg px-6"
          >
            <AccordionTrigger
              className="font-bold hover:no-underline"
              data-testid="faq-destacar"
            >
              ¿Qué límites operativos debo considerar por plan?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground">
              Los límites operativos por plan son:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li><strong>Básico:</strong> 1 propiedad publicada, sin propiedades destacadas.</li>
                <li><strong>Pro:</strong> hasta 10 propiedades publicadas, hasta 5 propiedades destacadas activas.</li>
                <li><strong>Asesor Inmobiliario:</strong> propiedades ilimitadas, hasta 10 destacadas activas.</li>
                <li><strong>Desarrolladora:</strong> desarrollos, unidades y propiedades ilimitados, hasta 10 destacadas activas.</li>
              </ul>
              <p className="mt-2">Inventario para Colaboración, leads, comisiones y herramientas comerciales dependen del rol y del estado activo de tu suscripción.</p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="item-7"
            className="bg-background border border-border rounded-lg px-6"
          >
            <AccordionTrigger
              className="font-bold hover:no-underline"
              data-testid="faq-suscripcion-inactiva"
            >
              ¿Qué pasa si mi suscripción está inactiva?
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground">
              Si el pago no está activo, se pausan las funciones premium del rol
              y la creación de nuevas publicaciones según plan. Tu información se
              conserva, puedes seguir viendo y gestionando inventario existente,
              y puedes reactivar cuando quieras.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <Footer />
    </div>
  );
};

export default Pricing;
